import { InfiniteData } from '@tanstack/react-query'

import { Embed, File, Image } from '@tribeplatform/gql-client/global-types'
import { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Chat,
  ChatList,
  ChatParticipant,
  ChatType,
  MessageContentType,
  MessagePreview,
  NewMessage,
} from '@tribeplatform/gql-client/types'

import { useQueryClient } from '../../index.js'
import {
  UseMutationOptions,
  useMutation,
} from '../../lib/react-query/useMutation.js'
import { useTribeClient } from '../../useTribeClient.js'
import {
  getChatKey,
  getChatsKey,
  getPendingMessagesKey,
  getUnreadMessagesCountKey,
} from '../../utils/keys/messaging.key.js'
import { useAuthMember } from '../auth/useAuthMember.js'
import { ARCHIVED_CHATS_QUERY } from './constants.js'
import { PendingMessage } from './usePendingMessages.js'
import { useRemovePendingMessage } from './useRemovePendingMessage.js'

type SendMessageToChatInput = {
  nonce: string
  content: string
  images: Image[]
  attachments: File[]
  embeds: Embed[]
  authorParticipantId: string
}

type Args = {
  chatId: string
  input: SendMessageToChatInput
}

export const useSendMessageToChat = (
  options: UseMutationOptions<NewMessage, ClientError, Args>,
) => {
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const { data: authMember } = useAuthMember()
  const { mutate: removePendingMessages } = useRemovePendingMessage()

  return useMutation<NewMessage, ClientError, Args>(
    (input: Args) =>
      client.messaging.sendMessageToChat({
        chatId: input.chatId,
        input: {
          content: input.input.content,
          nonce: input.input.nonce,
          imageIds: input.input.images.map(image => image.id),
          attachmentIds: input.input.attachments.map(
            attachment => attachment.id,
          ),
          embedIds: input.input.embeds.map(embed => embed.id),
        },
      }),
    {
      onMutate: args => {
        const pendingMessagesKey = getPendingMessagesKey({
          variables: { id: args.chatId },
        })
        queryClient.setQueryData<PendingMessage[]>(
          pendingMessagesKey,
          oldMessages => [
            ...(oldMessages || []),
            {
              _id: args.input.nonce,
              nonce: args.input.nonce,
              content: args.input.content,
              images: args.input.images,
              attachments: args.input.attachments,
              embeds: args.input.embeds,
              authorId: authMember?.id,
              authorParticipantId: args.input.authorParticipantId,
              contentType: MessageContentType.Html,
              sentAt: new Date().toISOString(),
              status: 'pending',
            },
          ],
        )
        if (options.onMutate) {
          options.onMutate(args)
        }
      },
      ...options,
      onSuccess: (data, variables, context) => {
        /**
         * Optimistically update the current user offset for the chat
         */
        const chatKey = getChatKey({
          variables: {
            id: variables.chatId,
          },
        })
        const chat = queryClient.getQueryData<Chat>(chatKey)
        if (chat) {
          const updatedParticipant = {
            ...(chat.currentParticipant ?? {}),
            lastReadMessage: {
              pageId: data.pageId,
              messageId: data.message._id,
              signature: data.message.addressSignature,
            },
          } as ChatParticipant

          const updatedLastMessage = {
            ...(chat.lastMessage ?? {}),
            pageId: data.pageId,
            messageId: data.message._id,
            addressSignature: data.message.addressSignature,
          } as MessagePreview

          const updatedChat: Chat = {
            ...chat,
            lastMessage: updatedLastMessage,
            currentParticipant: updatedParticipant,
          }

          queryClient.setQueryData<Chat>(chatKey, updatedChat)
          queryClient.invalidateQueries(
            getUnreadMessagesCountKey({
              variables: data.chatId,
            }),
          )

          /**
           * @todo
           * This is a temporary fix to unarchive the chat when a message is sent
           * We will remove this once there is an event sent for this on web socket.
           */
          const isArchived = !!chat.currentParticipant?.archivedAt
          const shouldUnarchive = isArchived && chat.type === ChatType.Direct
          if (shouldUnarchive) {
            const archivedChatsKey = getChatsKey({
              variables: {
                ...ARCHIVED_CHATS_QUERY,
              },
            })
            queryClient.setQueriesData(
              archivedChatsKey,
              (oldChats: InfiniteData<ChatList>): InfiniteData<ChatList> => {
                return {
                  ...oldChats,
                  pages: oldChats.pages.map(page => ({
                    ...page,
                    data: page.data.filter(
                      chat => chat._id !== variables.chatId,
                    ),
                  })),
                }
              },
            )
            queryClient.invalidateQueries(archivedChatsKey)
          }

          if (options.onSuccess) {
            options.onSuccess(data, variables, context)
          }
        }
        return data
      },
      onError: (error, variables, context) => {
        removePendingMessages({
          chatId: variables.chatId,
          nonces: [variables.input.nonce],
        })
        if (options?.onError) {
          options.onError(error, variables, context)
        }
      },
    },
  )
}
